// Settings → Browser: a self-contained setup guide for the browser extension — install,
// connect this session (pushes the token to the pinned extension, which exchanges it for its
// own session), and finish setup in the extension. Reachable on any Chromium browser even
// before the extension is installed, so it doubles as the install guide.
import { useState } from "react";
import { useT } from "../i18n";
import { backendOrigin } from "../api/client";
import { EXT_ID, extensionAvailable, pairExtension } from "./extension-bridge";

export function ConnectExtension({ login }: { login: string }) {
  const t = useT();
  const [state, setState] = useState<"idle" | "busy" | "done" | "error">("idle");
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const available = extensionAvailable();
  const crxUrl = `${backendOrigin()}/downloads/vault-extension.crx`;
  const zipUrl = `${backendOrigin()}/downloads/vault-extension.zip`;

  const connect = async () => {
    setState("busy");
    setErr(null);
    try {
      await pairExtension(login);
      setState("done");
    } catch (e) {
      setState("error");
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const copyId = () => {
    navigator.clipboard.writeText(EXT_ID).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-muted">
        {t("The browser extension autofills and saves logins into your personal vault, taking over from the browser's built-in password manager.")}
      </p>

      {/* Detection banner */}
      <div className={`rounded border px-2.5 py-1.5 text-xs ${available ? "border-ok/40 bg-ok/10 text-ok" : "border-border bg-panel2 text-muted"}`}>
        {available ? t("Extension detected in this browser. ✓") : t("Extension not detected yet — install it, then reload this page.")}
      </div>

      {/* Step 1 — install */}
      <Step n={1} title={t("Install the extension")} done={available}>
        <p className="text-muted">{t("On company-managed devices the extension installs automatically via policy — just wait for it to appear, then continue below. On other devices, use the ZIP + “Load unpacked” below.")}</p>
        <div className="flex flex-wrap items-center gap-2">
          <a href={zipUrl} download className="rounded bg-accent px-3 py-1.5 font-medium text-white hover:bg-accent2">{t("Download extension (.zip)")}</a>
          <a href={crxUrl} download className="rounded border border-border px-3 py-1.5 hover:border-accent">{t(".crx (for policy)")}</a>
          {!available && (
            <button onClick={() => location.reload()} className="rounded border border-border px-3 py-1.5 hover:border-accent">{t("Reload page")}</button>
          )}
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-muted">{t("Extension ID")}</span>
          <code className="rounded bg-panel2 px-1.5 py-0.5 font-mono text-fg">{EXT_ID}</code>
          <button onClick={copyId} className="text-accent hover:underline">{copied ? t("copied") : t("copy")}</button>
        </div>
        <details className="text-muted" open={!available}>
          <summary className="cursor-pointer select-none">{t("Manual install with the ZIP (Chrome / Edge)")}</summary>
          <ol className="mt-1.5 list-decimal space-y-1 pl-5">
            <li>{t("Download the .zip above and extract it to a permanent folder (don't delete it — the browser loads the extension from there).")}</li>
            <li>{t("Open edge://extensions (or chrome://extensions).")}</li>
            <li>{t("Turn on Developer mode (left side on Edge, top-right on Chrome).")}</li>
            <li>{t("Click “Load unpacked” and select the extracted folder (the one containing manifest.json).")}</li>
            <li>{t("Reload this page and click Connect below.")}</li>
          </ol>
          <p className="mt-1.5">{t("The .crx file is only for automated policy install — Chrome/Edge block installing it by drag-and-drop.")}</p>
        </details>
      </Step>

      {/* Step 2 — connect this session */}
      <Step n={2} title={t("Connect this session")} done={state === "done"}>
        {available ? (
          <>
            <p className="text-muted">{t("This links the extension to your account. Microsoft 365 users never type a password here — the connection reuses this session.")}</p>
            <button
              onClick={connect}
              disabled={state === "busy" || state === "done"}
              className="w-fit rounded bg-accent px-3 py-1.5 font-medium text-white hover:bg-accent2 disabled:opacity-50"
            >
              {state === "done" ? t("Connected ✓") : state === "busy" ? "…" : t("Connect browser extension")}
            </button>
            {state === "error" && <span className="text-danger">{t("Could not connect the extension.")} {err}</span>}
            <p className="text-[11px] text-muted">{t("Sessions last 12 hours; the extension reconnects automatically each time you open the vault.")}</p>
          </>
        ) : (
          <p className="text-muted">{t("Install the extension first (step 1), then reload this page.")}</p>
        )}
      </Step>

      {/* Step 3 — finish in the extension */}
      <Step n={3} title={t("Finish setup in the extension")} done={false}>
        <p className="text-muted">{t("Click the Vault icon in the browser toolbar (pin it for easy access), then:")}</p>
        <ol className="list-decimal space-y-1 pl-5 text-muted">
          <li>{t("Unlock with your master password — it never leaves your device.")}</li>
          <li>{t("Choose or create the personal-vault folder the browser will use.")}</li>
          <li>{t("Optionally import your existing passwords from a CSV export.")}</li>
          <li>{t("Turn off the browser's built-in password saving so it stops competing.")}</li>
        </ol>
      </Step>
    </div>
  );
}

function Step({ n, title, done, children }: { n: number; title: string; done: boolean; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px] font-bold ${done ? "bg-ok text-white" : "bg-panel2 text-muted"}`}>
        {done ? "✓" : n}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2 text-xs">
        <span className="text-sm font-medium text-fg">{title}</span>
        {children}
      </div>
    </div>
  );
}
